// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type StoreScorecardStatus = components["schemas"]["StoreScorecardStatusEnum"];
export type StoreScorecardCheck = components["schemas"]["StoreScorecardCheckResponse"];
export type StoreScorecardPanel = components["schemas"]["StoreScorecardPanelResponse"];
export type StoreSettingsDiff = components["schemas"]["StoreSettingsDiffResponse"];
export type StoreSettingsDiffRow = components["schemas"]["StoreSettingsDiffRowResponse"];
export type StoreSettingsDiffOrigin = components["schemas"]["StoreSettingsDiffOriginEnum"];

/**
 * The five scorecard panels, in display order.
 *
 * Each is a separate endpoint on purpose — they differ in cost by orders of
 * magnitude, so one panel must never block or blank the others.
 */
export const STORE_SCORECARD_PANELS = ["identity", "cameras", "planograms", "imports", "reporting"] as const;

export type StoreScorecardPanelName = (typeof STORE_SCORECARD_PANELS)[number];

const PANEL_ENDPOINTS = {
  identity: "/v1/stores/{store_name}/scorecard/identity",
  cameras: "/v1/stores/{store_name}/scorecard/cameras",
  planograms: "/v1/stores/{store_name}/scorecard/planograms",
  imports: "/v1/stores/{store_name}/scorecard/imports",
  reporting: "/v1/stores/{store_name}/scorecard/reporting",
} as const;

// A scorecard is a point-in-time read of live pipelines. Cached briefly so switching
// tabs is instant, but short enough that a refetch after a fix shows the change.
const SCORECARD_STALE_TIME = 60_000;

export function useStoreScorecardPanelQuery(storeName: string, panel: StoreScorecardPanelName) {
  return useQuery({
    queryKey: ["stores", "scorecard", storeName, panel],
    staleTime: SCORECARD_STALE_TIME,
    enabled: !!storeName,
    queryFn: async () => {
      const res = await api.GET(PANEL_ENDPOINTS[panel], {
        params: { path: { store_name: storeName } },
      });
      // An error status with an empty body leaves BOTH `error` and `data`
      // undefined, so checking `error` alone would resolve the query to
      // undefined and the card would then read `.checks` off it.
      if (res.error || !res.data) throw new Error(`Failed to load ${panel} scorecard panel`);
      return res.data as StoreScorecardPanel;
    },
  });
}

export function useStoreSettingsDiffQuery(storeName: string) {
  return useQuery({
    queryKey: ["stores", "scorecard", storeName, "settings-diff"],
    staleTime: SCORECARD_STALE_TIME,
    enabled: !!storeName,
    queryFn: async () => {
      const res = await api.GET("/v1/stores/{store_name}/scorecard/settings-diff", {
        params: { path: { store_name: storeName } },
      });
      if (res.error || !res.data) throw new Error("Failed to load settings diff");
      return res.data as StoreSettingsDiff;
    },
  });
}
